import { useRef, useCallback, useState, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';

const POLL_INTERVAL = 5000;

/**
 * Custom hook that encapsulates the entire task-polling + file-download lifecycle.
 *
 * Returns:
 *  - submitTask(endpoint, formData)  → kicks off the request & starts polling
 *  - cancelTask()                    → aborts everything and resets
 *  - loading       (boolean)
 *  - message       (string)
 *  - messageType   ('success' | 'error' | 'info' | '')
 */
const useTaskPolling = (onSuccess) => {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    const pollingRef = useRef(null);
    const abortRef = useRef(null);
    const downloadingRef = useRef(false);
    const taskIdRef = useRef(null);
    const onSuccessRef = useRef(onSuccess);

    useEffect(() => {
        onSuccessRef.current = onSuccess;
    }, [onSuccess]);

    /* ───── helpers ───── */

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
    }, []);

    const reset = useCallback(() => {
        stopPolling();
        taskIdRef.current = null;
        downloadingRef.current = false;
        setLoading(false);
        setMessage('');
        setMessageType('');
    }, [stopPolling]);

    const handleDownload = useCallback(async (response) => {
        downloadingRef.current = true;
        try {
            const filename =
                response.headers['content-disposition']
                    ?.split('filename=')[1]
                    ?.replace(/['"]/g, '') || 'report.docx';

            const blob = new Blob([response.data], {
                type: response.headers['content-type'],
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                setMessage('File downloaded successfully');
                setMessageType('success');
                if (onSuccessRef.current) onSuccessRef.current();
                setTimeout(reset, 500);
            }, 100);
        } catch {
            setMessage('Error downloading file');
            setMessageType('error');
        } finally {
            setLoading(false);
            downloadingRef.current = false;
            stopPolling();
        }
    }, [reset, stopPolling]);

    const pollOnce = useCallback(async () => {
        if (downloadingRef.current || !taskIdRef.current) return;

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const response = await axiosInstance.get(
                `/api/task-status/${taskIdRef.current}`,
                { responseType: 'blob', signal: controller.signal },
            );

            const contentType = response.headers['content-type'];

            if (contentType?.includes('application/json')) {
                const text = await response.data.text();
                const data = JSON.parse(text);

                if (data.status === 'completed') {
                    stopPolling();
                    const fileResponse = await axiosInstance.get(
                        `/api/task-status/${taskIdRef.current}`,
                        { responseType: 'blob' },
                    );
                    await handleDownload(fileResponse);
                } else if (data.status === 'failed') {
                    stopPolling();
                    setMessage(`Error: ${data.message}`);
                    setMessageType('error');
                    setLoading(false);
                }
            } else if (
                contentType?.includes('application/vnd.openxmlformats') ||
                contentType?.includes('application/pdf')
            ) {
                stopPolling();
                await handleDownload(response);
            }
        } catch (error) {
            if (error.name !== 'CanceledError') {
                stopPolling();
                setMessage('Error checking task status');
                setMessageType('error');
                setLoading(false);
            }
        }
    }, [handleDownload, stopPolling]);

    /* ───── public API ───── */

    const submitTask = useCallback(
        async (endpoint, formData) => {
            setLoading(true);
            setMessage('');
            setMessageType('');
            stopPolling();
            downloadingRef.current = false;

            try {
                const response = await axiosInstance.post(endpoint, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                if (response.status === 200) {
                    const { task_id } = response.data;
                    taskIdRef.current = task_id;
                    setMessage('Processing your request...');
                    setMessageType('info');

                    // Immediate first check, then interval
                    pollOnce();
                    pollingRef.current = setInterval(pollOnce, POLL_INTERVAL);
                }
            } catch (error) {
                stopPolling();
                if (error.response) {
                    setMessage(
                        `Error: ${error.response.data?.message || error.response.statusText}`,
                    );
                } else if (error.request) {
                    setMessage('No response from server');
                } else {
                    setMessage(`Error: ${error.message}`);
                }
                setMessageType('error');
                setLoading(false);
            }
        },
        [pollOnce, stopPolling],
    );

    const cancelTask = useCallback(() => {
        reset();
    }, [reset]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopPolling();
            downloadingRef.current = false;
        };
    }, [stopPolling]);

    return { submitTask, cancelTask, loading, message, messageType };
};

export default useTaskPolling;

